import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePermissions } from '@/features/auth';
import { DateRangePicker, lastNDays } from '@/shared/components/date-range-picker';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { DEFAULT_TIME_ZONE, NON_MARKET_TIME_NOTE } from '@/shared/lib/time-zone';

import { FinancialPartnerShare } from '../components/financial-partner-share';
import type { ReportParams } from '../types/report.types';

type Grouping = 'day' | 'month';

/**
 * The partner report (reports.partner.read, RBAC role specification, Role 5):
 * the partner share per day or month in the partner's countries — orders,
 * collected, share %, the share, what refunds gave back and the net share. The
 * API scopes it to the partner's countries and leaves out gross profit,
 * management fee and net profit unless the reader also holds reports.financial.read.
 * Period and grouping live in the URL.
 */
export function PartnerReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  const fallback = useMemo(() => lastNDays(30, DEFAULT_TIME_ZONE), []);
  const range = {
    from: searchParams.get('from') ?? fallback.from,
    to: searchParams.get('to') ?? fallback.to,
  };
  const groupBy: Grouping = searchParams.get('group') === 'month' ? 'month' : 'day';
  const params: ReportParams = { from: range.from, to: range.to, groupBy };

  const update = useCallback(
    (changes: Record<string, string | null>) =>
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Partner report"
        description={`Your share ${can('reports.financial.read') ? 'of the net profit ' : ''}on completed orders in your countries, and what refunds gave back ${NON_MARKET_TIME_NOTE}`}
        actions={
          <span data-print="hide">
            <DateRangePicker
              value={range}
              timeZone={DEFAULT_TIME_ZONE}
              onChange={(value) => update({ from: value.from, to: value.to })}
            />
          </span>
        }
      />

      <div className="flex flex-wrap items-center gap-2" data-print="hide">
        <Select
          value={groupBy}
          onValueChange={(value) => update({ group: value === 'month' ? 'month' : null })}
        >
          <SelectTrigger size="sm" className="h-8 w-36 text-xs" aria-label="Group by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day" className="text-xs">
              By day
            </SelectItem>
            <SelectItem value="month" className="text-xs">
              By month
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <FinancialPartnerShare params={params} filtered={false} onClearFilters={() => {}} />
    </PageContainer>
  );
}
